import React, { useEffect, useRef, useState } from 'react';
import "./NospForm.css";
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import Button from "../../../Button/Button";
import Modal from "../../Modal";
import * as api from "../../../api";
import LoadingSpinner from "../../../storybook/LoadingSpinner";
import { useDispatch, useSelector } from 'react-redux';
import { FinancialReferralNospDetailsRes, saveNospDetailsPayload, saveNOSPReferenceData } from "../../../ducks/redux/actions/FinancialReferral";

type ResponseOption = {
  id: number;
  text: string;
};

type DependsOn = {
  questionId: number;
  responseId: number;
};

type Question = {
  questionId: number;
  label: string;
  sequence: number;
  type: 'Radio' | 'Date';
  allowedChannels: string[];
  responseOptions?: ResponseOption[];
  dependsOn?: DependsOn[];
};

type QuestionsData = {
  namespace: {
    name: string;
    keyValues: Record<string, Question>;
  };
};

type NospEntry =
  | { questionId: number; type: 'Radio'; responseId: number }
  | { questionId: number; type: 'Date'; value: string };

interface NospFormProps extends RouteComponentProps<any> { }

const NospForm: React.FC<NospFormProps> = (props: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const hasNospFromRedux = useRef(false);

  /** Local UI state (controlled) */
  const [formState, setFormState] = useState<Record<number, number>>({}); // Radio answers
  const [dateFields, setDateFields] = useState<Record<number, string>>({}); // Date answers
  const [errorFields, setErrorFields] = useState<Record<number, boolean>>({});
  const [errorSections, setErrorSections] = useState<Record<number, boolean>>({});
  const [responseData, setResponseData] = useState<QuestionsData>();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [rootChildrenError, setRootChildrenError] = useState(false);

  // --- Safari-safe local date range (no UTC shift) ---
  const formatLocalDate = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const today = new Date();
  const minDate = formatLocalDate(today);

  const max = new Date();
  max.setFullYear(max.getFullYear() + 2);
  const maxDate = formatLocalDate(max);

  const COURT_QUESTION_ID = 2;
  const COURT_DATE_ID = 3;
  const EVICTION_QUESTION_ID = 5;
  const EVICTION_DATE_ID = 6;

  // --- Manual typing prevention + clamping helpers ---
  // Strict YYYY-MM-DD check (no partials)
  const isValidYYYYMMDD = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

  // Lexicographic comparison works for YYYY-MM-DD
  const clampDateString = (value: string, min: string, max: string) => {
    if (!isValidYYYYMMDD(value)) return value;
    if (value < min) return min;
    if (value > max) return max;
    return value;
  };

  const addDaysToDateString = (value: string, days: number) => {
    const date = new Date(`${value}T00:00:00`);
    date.setDate(date.getDate() + days);
    return formatLocalDate(date);
  };

  const addYearsToDateString = (value: string, years: number) => {
    const date = new Date(`${value}T00:00:00`);
    date.setFullYear(date.getFullYear() + years);
    return formatLocalDate(date);
  };

  // Block manual typing; allow navigation keys for accessibility
  const preventManualTyping = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed = new Set([
      "Tab", "Shift", "Control", "Alt", "Meta",
      "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
      "Home", "End"
    ]);
    if (allowed.has(e.key)) return;
    e.preventDefault();
  };

  const preventPasteDrop = (e: React.ClipboardEvent | React.DragEvent) => {
    e.preventDefault();
  };

  // Try to open the native picker on focus (Chrome/Edge). iOS Safari shows its picker automatically.
  const openPickerOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // @ts-ignore
    if (typeof e.currentTarget.showPicker === "function") {
      // @ts-ignore
      e.currentTarget.showPicker();
    }
  };

  /** Redux data */
  const nospReferenceData = useSelector(
    (state: any) => state.financialReferralReducer?.nospReferenceData,
  );

  const data = useSelector(
    (state: any) => state.financialReferralReducer?.financialReferralData
  );
  const financialData = data || {};

  /** -------- Data fetch: NOSP questions schema -------- */
  const fetchRefData = async () => {
    try {
      let referenceDataToUse: any =
        Array.isArray(nospReferenceData) &&
          nospReferenceData.length > 0
          ? nospReferenceData
          : null;
      if (!referenceDataToUse) {
        referenceDataToUse = await api.getFinancialReferralNOSPData();
      }
      dispatch(saveNOSPReferenceData(referenceDataToUse));
      const questionData: QuestionsData = referenceDataToUse[0];
      setResponseData(questionData);
    } catch (e) {
      console.error("Failed to load schema:", e);
      setResponseData(undefined);
    } finally {
      setLoading(false);
    }
  };

  /** -------- Hydrate UI from Redux.NospDetails (if present) -------- */
  const getNospFromRedux = (nospDetails: NospEntry[]) => {
    const nextRadios: Record<number, number> = {};
    const nextDates: Record<number, string> = {};
    nospDetails?.forEach((item) => {
      if (item?.type === 'Radio' && item.questionId && (item as any).responseId) {
        nextRadios[item.questionId] = (item as any).responseId;
      }
      if (item?.type === 'Date' && item.questionId && (item as any).value) {
        nextDates[item.questionId] = (item as any).value;
      }
    });
    setFormState(nextRadios);
    setDateFields(nextDates);
  };

  /** -------- Persist UI -> Redux on any change -------- */
  const syncNospToRedux = (
    nextForm: Record<number, number>,
    nextDates: Record<number, string>
  ) => {
    const nospArray: NospEntry[] = [
      ...Object.entries(nextForm).map(([questionId, responseId]) => ({
        questionId: Number(questionId),
        type: 'Radio' as const,
        responseId,
      })),
      ...Object.entries(nextDates).map(([questionId, value]) => ({
        questionId: Number(questionId),
        type: 'Date' as const,
        value,
      })),
    ];
    dispatch(FinancialReferralNospDetailsRes(nospArray));
  };

  useEffect(() => { 
    if (!hasNospFromRedux.current) {
      const nospDetails = financialData?.NospDetails;
      if (Array.isArray(nospDetails) && nospDetails.length > 0) {
        hasNospFromRedux.current = true;
        getNospFromRedux(nospDetails as NospEntry[]);
      }
    }
    fetchRefData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** -------- Helpers -------- */
  const isMainOptionNotSelected = (): boolean => !formState[1];

  const isRootNodeP360Allowed = (): boolean => {
    const rootNode = responseData ?? undefined;
    if (!rootNode) return false;
    return rootNode.namespace.keyValues[1].allowedChannels.includes("P360");
  };

  const isQuestionAllowed = (question: Question): boolean =>
    question.allowedChannels.includes('P360');

  const isQuestionVisible = (question: Question): boolean => {
    if (!isQuestionAllowed(question)) return false;
    if (!question.dependsOn || question.dependsOn.length === 0) {
      return true;
    }
    return question.dependsOn.every((dep) => {
      const parentAnswer = formState[dep.questionId];
      return parentAnswer === dep.responseId;
    });
  };

  const getChildQuestions = (questionId: number, responseId: number): Question[] => {
    if (!responseData?.namespace?.keyValues) return [];
    const all = Object.values(responseData.namespace.keyValues);
    return all.filter(
      (q: Question) =>
        isQuestionAllowed(q) &&
        q.dependsOn?.some((dep) => dep.questionId === questionId && dep.responseId === responseId)
    );
  };

  const hasUnansweredRootChildren = (state: Record<number, number>): boolean => {
    // Root Yes = option id "1"
    if (state[1] !== 1) return false;
    const allQuestions = Object.values(responseData?.namespace?.keyValues || {});
    // Get all child Radio questions that open on Root Yes AND are currently visible
    const visibleRootChildren = allQuestions.filter(q =>
      q.type === "Radio" &&
      q.questionId !== 1 &&
      isQuestionVisible(q) &&
      q.dependsOn?.some(dep =>
        dep.questionId === 1 && dep.responseId === 1
      )
    );
    if (!visibleRootChildren.length) return false;
    //  Each visible child must have a selection
    const allChildrenAnswered = visibleRootChildren.every(
      q => state[q.questionId] !== undefined
    );
    return !allChildrenAnswered;
  };

  const clearDescendants = (
    questionId: number,
    next: Record<number, number>,
    nextDates: Record<number, string>
  ) => {
    if (!responseData?.namespace?.keyValues) return;
    Object.values(responseData.namespace.keyValues).forEach((q) => {
      if (q.dependsOn?.some((dep) => dep.questionId === questionId)) {
        delete next[q.questionId];
        delete nextDates[q.questionId];
        clearDescendants(q.questionId, next, nextDates);
      }
    });
  };

  const getDateRange = (questionId: number) => {
    if (
      questionId === EVICTION_DATE_ID &&
      formState[COURT_QUESTION_ID] === 1 &&
      isValidYYYYMMDD(dateFields[COURT_DATE_ID] || "")
    ) {
      const evictionMinDate = addDaysToDateString(dateFields[COURT_DATE_ID], 1);
      return {
        min: evictionMinDate,
        max: addYearsToDateString(evictionMinDate, 2),
      };
    }

    return {
      min: minDate,
      max: maxDate,
    };
  };


  const handleRadioChange = (questionId: number, responseId: number) => {
  if (questionId === 1) {
    setError(false);
  }
  const next = {
    ...formState,
    [questionId]: responseId,
  };
  const nextDates = { ...dateFields };
  clearDescendants(questionId, next, nextDates);

  // If root is "No" (id="2") OR "Don't know" (id="3"), clear ALL dates (state + Redux)
  if (questionId === 1) {
    const isNoOrDontKnow = responseId === 2 || responseId === 3;

    if (isNoOrDontKnow) {
      // Clear date state and errors
      setDateFields({});
      setErrorFields({});
      setErrorSections({});
      setRootChildrenError(false);     
      // Sync cleared dates to Redux immediately
      syncNospToRedux(next, {});
      // Keep root selection in state; descendants already cleared above
      setFormState(next);
      return; // avoid re-sync below with stale dates
    }
  }

  setFormState(next);
  setDateFields(nextDates);

  if (rootChildrenError && !hasUnansweredRootChildren(next)) {
    setRootChildrenError(false);
  }

  // Persist to Redux
  syncNospToRedux(next, nextDates);
};

  const handleDateChange = (question: Question, value: string) => {
    const qid = question.questionId;
    const showErr = !value;
    const nextDates = { ...dateFields, [qid]: value };
    const nextForm = { ...formState };

    if (qid === COURT_DATE_ID) {
      delete nextForm[EVICTION_QUESTION_ID];
      delete nextDates[EVICTION_DATE_ID];
    }

    setDateFields(nextDates);
    setFormState(nextForm);
    syncNospToRedux(nextForm, nextDates);

    setErrorFields((prev) => {
      const next = { ...prev, [qid]: showErr };
      if (qid === COURT_DATE_ID) {
        delete next[EVICTION_DATE_ID];
      }
      return next;
    });

    if (qid === COURT_DATE_ID) {
      setErrorSections((prev) => {
        const next = { ...prev };
        delete next[EVICTION_QUESTION_ID];
        return next;
      });
    }

    if (value && question.dependsOn && question.dependsOn.length > 0) {
      const parentId = question.dependsOn[0].questionId;
      setErrorSections((prev) => ({ ...prev, [parentId]: false }));
    }
  };

  const buildNospDetailsPayload = (
    radios: Record<number, number>,
    dates: Record<number, string>,
    schema?: QuestionsData
  ) => {
    if (!schema?.namespace?.keyValues) return [];

    const root = schema.namespace.keyValues[1]; // Root question
    const rootSelectedId = radios[1];

    if (!rootSelectedId) return []; // nothing selected

    const rootOptionText =
      root.responseOptions?.find(o => o.id === rootSelectedId)?.text ??
      rootSelectedId;

    if (rootOptionText === "No" || rootOptionText === "Don't know") {
      return [
        {
          questionId: 1,
          label: root.label,
          responses: [
            {
              id: rootSelectedId,
              value: rootOptionText,
            },
          ],
        },
      ];
    }

    const questions = Object
      .values(schema.namespace.keyValues)
      .filter(q => q.allowedChannels?.includes("P360"))
      .sort((a, b) => a.sequence - b.sequence);

    const payload: any[] = [];

    for (const q of questions) {
      if (q.type === "Radio") {
        const selectedId = radios[q.questionId];
        if (!selectedId) continue;

        const text = q.responseOptions?.find(o => o.id === selectedId)?.text ?? selectedId;

        payload.push({
          questionId: q.questionId,
          label: q.label,
          responses: [
            { id: selectedId, value: text }
          ]
        });
      }

      if (q.type === "Date") {
        const value = dates[q.questionId];
        if (!value) continue;

        const [yyyy, mm, dd] = value.split("-");
        const formattedDate = `${dd}/${mm}/${yyyy}`;

        payload.push({
          questionId: q.questionId,
          label: q.label,
          responses: [
            { value: formattedDate }
          ]
        });
      }
    }

    return payload;
  };

  const onContinueHandler = () => {
    if (isMainOptionNotSelected()) {
      setError(true);
      return;
    }
    if (hasUnansweredRootChildren(formState)) {
      setRootChildrenError(true);
      return;
    } else {
      setRootChildrenError(false);
    }

    if (!responseData?.namespace?.keyValues) {
      return;
    }

    let dateRequired = false;
    let dateMissing = false;
    const newErrFields: Record<number, boolean> = {};
    const newErrSections: Record<number, boolean> = {};

    Object.values(responseData.namespace.keyValues).forEach((q) => {
      if (q.type === "Date" && isQuestionVisible(q)) {
        dateRequired = true;
        if (!dateFields[q.questionId]) {
          dateMissing = true;
          newErrFields[q.questionId] = true;
          if (q.dependsOn?.length) {
            newErrSections[q.dependsOn[0].questionId] = true;
          }
        }
      }
    });

    setErrorFields(newErrFields);
    setErrorSections(newErrSections);
    if (dateRequired && dateMissing) return;
    //save payload
    const nospDetailsPayload = buildNospDetailsPayload(formState, dateFields, responseData);
    dispatch(saveNospDetailsPayload(nospDetailsPayload));
    props.history.push("/referraladditionaldetails");
  };

  const renderDateField = (question: Question) => {
    const value = dateFields[question.questionId] || "";
    const showError = errorFields[question.questionId];
    const { min, max } = getDateRange(question.questionId);

    return (
      <div className="cal-field">
        {showError && (
          <div className=" mt-2 mb-2">
            <p className="nosp-error-text">{t("FI_Nosp_errorMsg")}</p>
          </div>
        )}
        <label className="cal-label bold-label" htmlFor={`date-${question.questionId}`}>
          {question.label}
        </label>

        <input
          id={`date-${question.questionId}`}
          type="date"
          className={`date-input${showError ? " date-input-error" : ""}`}
          min={min}
          max={max}
          value={value}
          //  Prevent manual typing, paste, and drag/drop
          onKeyDown={preventManualTyping}
          onPaste={preventPasteDrop}
          onDrop={preventPasteDrop}
          inputMode="none"          // suppress mobile keyboard
          onFocus={openPickerOnFocus}
          //  Clamp to [minDate, maxDate] and keep your existing state/Redux sync
          onChange={(e) => {
            const raw = e.target.value || "";
            if (!raw) {
              handleDateChange(question, "");
              return;
            }
            if (!isValidYYYYMMDD(raw)) {
              // Ignore invalid formats (manual typing is blocked anyway)
              return;
            }
            const clamped = clampDateString(raw, min, max);
            handleDateChange(question, clamped);
          }}
        />
      </div>
    );
  };

  const renderRadioQuestion = (question: Question, depth: number) => {
    const selected = formState[question.questionId];

    return (
      <div className="question-block global-txt mb-4" data-depth={depth}>
        {depth > 0 && <div className="question-text">{question.label}</div>}
        <div className="radio-group">
          {question.responseOptions?.map((option) => {
            const optionId = `q-${question.questionId}-opt-${option.id}`;
            const isSelected = selected === option.id;
            const isYesSelected = "Yes" === option.text && isSelected;
            const childQs = getChildQuestions(question.questionId, option.id);
            const isRootYes = depth === 0 && question.questionId === 1 && option.id === 1;

            return (
              <div
                key={option.id}
                className={`radio-option-block ${isRootYes && isYesSelected ? "has-stem" : ""}`}
              >
                <label className="radio-option" htmlFor={optionId}>
                  <input
                    id={optionId}
                    type="radio"
                    className="radio-input"
                    name={String(question.questionId)}
                    value={option.id}
                    checked={isSelected}
                    onChange={() => handleRadioChange(question.questionId, option.id)}
                  />
                  <span className={`ml-1 radio-label${isSelected ? " selected-label" : ""}`}>
                    {option.text}
                  </span>
                </label>

                {isSelected && childQs.length > 0 && (
                  <div className={`radio-child ${isRootYes ? "stem" : ""}`}>
                    {depth === 0 && rootChildrenError && (
                      <div className="mt-2 mb-2">
                        <p className="nosp-error-text">
                          {t("FI_Nosp_childerrorMsg")}
                        </p>
                      </div>
                    )}
                    {depth === 0 ? (
                      childQs.map((q) => (
                        <div key={q.questionId} className="nested-item">
                          {renderQuestion(q, depth + 1)}
                        </div>
                      ))
                    ) : (
                      <div
                        className={`nested-section${errorSections[question.questionId] ? " nested-section-error" : ""
                          }`}
                      >
                        {childQs.map((q) => (
                          <div key={q.questionId} className="nested-item">
                            {renderQuestion(q, depth + 1)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderQuestion = (question: Question, depth: number) => {
    if (!isQuestionVisible(question)) return null;
    if (question.type === "Date") return renderDateField(question);
    if (question.type === "Radio") return renderRadioQuestion(question, depth);
    return null;
  };

  const onBackHandler = () => {
  const referralReasons = Array.isArray(financialData.ReferralReason)
      ? financialData.ReferralReason
      : [];
    if (referralReasons.includes("Debt")) {
      props.history.push("/referraldebtquestion");
    } else {
      props.history.push("/referralreason");
    }   
  } 

  const exitButtonHandler = () => props.history.push("/Customer360View");
  const rootQ = responseData?.namespace?.keyValues[1] ?? null;

  return (
    <div className="container sm-ml2-mr1 ctn-fht global-txt">
      {isLoading ? (
        <div className="div-loader marign-left-smallscreen-0">
          <div className="SpinnerWrapper" id="SpinnerWrapper">
            <LoadingSpinner />
          </div>
        </div>
      ) : (
        <>
          <div id="backmodal-div1" className="mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0">
            <p id="backmodal-p" className="text-teal text-xl mt-4 mb-8 sm-pl-1 padding-0-smallscreen">
              <button data-testid="custBackBtn" className="back-btn" onClick={onBackHandler}>
                <i className="fas fa-angle-left"></i>
                <span>{t("back")}</span>
              </button>
            </p>
          </div>

          {isRootNodeP360Allowed() && (
            <div className="sm-ml2-mr1 px-2 h5 padding-0-smallscreen">
              <h1 className="mt-2 text-purple-100 text-2xl mb-4 global-txt">
                {rootQ?.label}
              </h1>

              {error && (
                <div className=" mt-2 mb-4 ">
                  <p className="nosp-error-text global-txt">
                    {t("FI_NOSP_Error")}
                  </p>
                </div>
              )}

              {rootQ && renderQuestion(rootQ, 0)}
            </div>
          )}

          <div className="pb-4 md:pb-8 px-2 mt-5 button-right-margin" id="nosp-btn-row">
            {/* Exit and Continue button row */}
            <div className="flex justify-between items-center pb-4 md:pb-8 px-2" id="nosp-btn-row">
              <button
                type="button"
                className="exit-btn link-like-btn"
                style={{ cursor: "pointer", background: "none", border: "none", padding: 0, textDecoration: "underline" }}
                onClick={() => setIsOpen(true)}
                aria-label={t("Exit")}
              >
                {t("Exit")}
              </button>
              <Button
                id="continue"
                type="button"
                data-testid="continue-button"
                name="Continue"
                onClick={onContinueHandler}
                value="Continue"
                className="button-class"
              >
                {t("Continue")}
                <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
              </Button>
            </div>
            <Modal open={isOpen} cssClasses="modalPopUp">
              <div id="exitmodal-div1" className="modalPopUpContainer container my-1">
                <div
                  id="exitmodal-div2"
                  className="py-lg-0 py-sm-0 h5 my-5 question-font-size text-center global-txt"
                >
                  {t("SSR030ai")}
                  <br />
                  <br />
                  {t("SSR030aii")}
                </div>
                <div
                  id="exitmodal-div3"
                  className="modalButtonContainer button-positions flex flex-wrap text-center pad-bot pt-lg-0"
                >
                  <Button id="exitmodal-btn2" className="button-class" onClick={() => setIsOpen(false)}>
                    {t("Financial_ReturnToMyReferral")}
                    <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
                  </Button>
                  <Button id="exitmodal-btn3" onClick={exitButtonHandler} className="button-class">
                    {t("leave_button")}
                    <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
                  </Button>
                </div>
              </div>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

export default NospForm;